import { useAlert } from '@genial/components';
import { Service } from '@genial/services';
import { ErrorResponse } from '@genial/services/src';
import * as ExpoFileSystem from 'expo-file-system';
import { Platform, Share } from 'react-native';
import { useSetRecoilState } from 'recoil';

import { useToastCreator } from '../../../hooks/useToastCreator';
import { updateIsExportingPdfForCustomer } from '../../customer/selector';
import { screenName } from '../constants';

import {
  updateReportFilterParamsSelector,
  updateReportMarketOptionSelector,
  updateReportMonthSelectedOptionsSelector,
  updateReportPeriodDatesSelector,
  updateReportPeriodOptionSelector,
  updateReportTypeOptionSelector,
  updateReportYearOptionSelector,
  updateReportPeriodYearOptionSelector,
  updateReportComplementaryTitleOptionSelector,
  updateReportYearBrokerageOptionSelector,
} from './selectors';
import {
  DownloadIncomeTaxComplementaryReportParams,
  ReportFitlerParams,
  ReportPeriodDates,
} from './types';

export const useReportsActions = () => {
  const { alert, remove } = useAlert();
  const { showToast } = useToastCreator('download-income-tax-complementary-report-toast');

  const setIsExportingPdfForCustomer = useSetRecoilState(updateIsExportingPdfForCustomer);
  const setReportTypeOption = useSetRecoilState(updateReportTypeOptionSelector);
  const setReportComplementaryTitleOption = useSetRecoilState(
    updateReportComplementaryTitleOptionSelector
  );
  const setReportMarketOption = useSetRecoilState(updateReportMarketOptionSelector);
  const setReportPeriodOption = useSetRecoilState(updateReportPeriodOptionSelector);
  const setReportPeriodDates = useSetRecoilState(updateReportPeriodDatesSelector);
  const setReportYearOption = useSetRecoilState(updateReportYearOptionSelector);
  const setReportPeriodYearOption = useSetRecoilState(updateReportPeriodYearOptionSelector);
  const setReportYearBrokerageOption = useSetRecoilState(updateReportYearBrokerageOptionSelector);
  const setReportMonthSelectedOptions = useSetRecoilState(updateReportMonthSelectedOptionsSelector);
  const setReportFilterParams = useSetRecoilState(updateReportFilterParamsSelector);

  const updateReportTypeOption = (value: string) => {
    setReportTypeOption(value);
  };

  const updateReportMarketOption = (value: string) => {
    setReportMarketOption(value);
  };

  const updateReportPeriodOption = (value: string) => {
    setReportPeriodOption(value);
  };

  const updateReportComplementaryTitleOption = (value: string) => {
    setReportComplementaryTitleOption(value);
  };

  const updateReportYearOption = (value: string) => {
    setReportYearOption(value);
  };

  const updateReportPeriodYearOption = (value: string) => {
    setReportPeriodYearOption(value);
  };

  const updateReportYearBrokerageOption = (value: string) => {
    setReportYearBrokerageOption(value);
  };

  const updateReportMonthSelectedOption = (value: string) => {
    setReportMonthSelectedOptions(value);
  };

  const updateReportPeriodDates = (value: ReportPeriodDates) => {
    setReportPeriodDates(value);
  };

  const updateReportFilterParams = (value: Partial<ReportFitlerParams>) => {
    setReportFilterParams(value);
  };

  const handleDownloadIncomeTaxComplementaryReport = async ({
    yearReference,
    fileTitle,
    reportType,
    accountNumber,
    sharingType = 'Download',
  }: DownloadIncomeTaxComplementaryReportParams) => {
    setIsExportingPdfForCustomer(true);

    const handleDownloadError = (error?: ErrorResponse) => {
      showToast(
        'error',
        'Não foi possível processar sua solicitação.',
        error?.errorMessage || 'Não há informações disponíveis no momento.'
      );
    };

    try {
      const { data } = await Service.Finance.V1.getIncomeTaxByComplementaryReportAccountNumber({
        yearReference: Number(yearReference),
        reportType: Number(reportType),
        sharingType,
        screenName,
        accountNumber,
      });

      const fileBase = data?.complementaryReport?.fileBase?.trim() || '';
      const fileType = data?.complementaryReport?.fileType?.trim() || '';
      const notificationSent = Boolean(data?.notificationSent);

      if (!fileBase && notificationSent) {
        showToast(
          'success',
          'Extrato enviado com sucesso.',
          'O extrato foi enviado para o email do cliente.'
        );
        return;
      }

      if (!fileBase || !fileType) {
        handleDownloadError();
        return;
      }

      if (Platform.OS === 'web') {
        const byteCharacters = atob(fileBase);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: `application/${fileType || 'pdf'}` });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileTitle} ${yearReference}.${fileType}`);
        document.body.appendChild(link);
        link.click();
      } else {
        const uri = `${ExpoFileSystem.cacheDirectory}${fileTitle} ${yearReference}.${fileType}`;
        await ExpoFileSystem.writeAsStringAsync(uri, fileBase, {
          encoding: ExpoFileSystem.EncodingType.Base64,
        });

        await Share.share({
          url: uri,
          title: `${fileTitle} ${yearReference}.${fileType}`,
        });
      }
    } catch (error) {
      handleDownloadError(error as ErrorResponse);
    } finally {
      setIsExportingPdfForCustomer(false);
    }
  };

  const handleDownloadBrokerageInvoiceFile = async ({
    invoiceDate,
    fileName,
    accountNumber,
  }: {
    invoiceDate: Date;
    fileName: string;
    accountNumber: number;
  }) => {
    alert({
      icon: 'BackInTime',
      iconColor: 'brand.tertiary.principal',
      title: 'Aviso',
      description:
        'Esta solicitação pode levar alguns segundos. Assim que o download for\nfinalizado, o arquivo será baixado automaticamente.',
    });

    const handleDownloadError = () => {
      alert({
        description: `Não foi possível realizar o download do arquivo\n${fileName}.`,
        icon: 'Warning',
        iconColor: 'feedback.attention.default',
      });
    };

    try {
      const { data } = await Service.Finance.V1.getBrokerageByInvoicesAccountNumberInvoiceDateFile({
        invoiceDate,
        screenName,
        accountNumber,
      });

      remove();

      if (!data?.brokerageInvoice?.invoice || data?.brokerageInvoice?.invoice === '') {
        handleDownloadError();
        return;
      }

      const { invoice } = data.brokerageInvoice;

      if (Platform.OS === 'web') {
        const byteCharacters = atob(invoice);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
      } else {
        const uri = `${ExpoFileSystem.cacheDirectory}${fileName}`;
        await ExpoFileSystem.writeAsStringAsync(uri, invoice, {
          encoding: ExpoFileSystem.EncodingType.Base64,
        });

        await Share.share({
          url: uri,
          title: fileName,
        });
      }
    } catch (_error) {
      handleDownloadError();
    }
  };

  return {
    updateReportTypeOption,
    updateReportMarketOption,
    updateReportPeriodOption,
    updateReportPeriodDates,
    updateReportYearOption,
    updateReportMonthSelectedOption,
    updateReportFilterParams,
    updateReportPeriodYearOption,
    updateReportYearBrokerageOption,
    updateReportComplementaryTitleOption,
    handleDownloadIncomeTaxComplementaryReport,
    handleDownloadBrokerageInvoiceFile,
  };
};
